/* AI Property Valuation panel for the Lead Detail page.
 *
 * Shows ARV range (low/mid/high), Repair budget, Assignment fee, and MAO
 * (Max Allowable Offer) using the 70% rule. Plus Claude's reasoning and
 * the comps' "key factors" bullet list.
 *
 * Has a "Run AI Valuation" button → calls /api/leads/{id}/ai-valuation
 * → persists fields on the lead → updates UI inline.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, Loader2, Sparkles, TrendingDown, DollarSign } from "lucide-react";
import { openUpgradeModal } from "@/components/UpgradeToProModal";

const fmtMoney = (n) =>
  typeof n === "number"
    ? `$${Math.round(n).toLocaleString()}`
    : "—";

export default function AIValuationPanel({ lead, onUpdate }) {
  const [running, setRunning] = useState(false);

  const hasValuation = typeof lead?.ai_arv_mid === "number";

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/ai-valuation`);
      toast.success(`ARV ${fmtMoney(data.arv_mid)} · MAO ${fmtMoney(data.mao)}`);
      onUpdate?.({
        ai_arv_low: data.arv_low,
        ai_arv_mid: data.arv_mid,
        ai_arv_high: data.arv_high,
        ai_repair_estimate: data.repair_estimate,
        ai_assignment_fee: data.assignment_fee,
        ai_mao: data.mao,
        ai_valuation_confidence: data.confidence,
        ai_valuation_reasoning: data.reasoning,
        ai_valuation_key_factors: data.key_factors,
        ai_valuation_comp_count: data.comp_count,
        ai_valuated_at: data.valuated_at,
      });
    } catch (e) {
      if (e?.response?.status === 402) {
        openUpgradeModal({ feature: "valuation" });
      } else {
        toast.error(e?.response?.data?.detail || "Valuation failed");
      }
    } finally {
      setRunning(false);
    }
  };

  const confidence = lead?.ai_valuation_confidence;
  const confBg =
    confidence === "high"
      ? "bg-emerald-100 text-emerald-800"
      : confidence === "low"
      ? "bg-rose-100 text-rose-800"
      : "bg-amber-100 text-amber-800";

  return (
    <div
      className="card border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 mb-5"
      data-testid="ai-valuation-panel"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Calculator size={18} className="text-emerald-700" />
          <div>
            <div className="label-eyebrow text-emerald-700">AI Valuation</div>
            <div className="font-display font-bold text-lg leading-tight">
              ARV + Max Offer (70% rule)
            </div>
          </div>
          {confidence && (
            <span
              className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ml-2 ${confBg}`}
            >
              {confidence} confidence
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={run}
          disabled={running}
          data-testid="run-ai-valuation-btn"
          className="btn-money text-sm flex items-center gap-1.5"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Crunching…" : hasValuation ? "Re-run" : "Run Valuation"}
        </button>
      </div>

      {hasValuation ? (
        <>
          {/* Big-number row */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Stat
              label="ARV mid"
              value={fmtMoney(lead.ai_arv_mid)}
              sub={`${fmtMoney(lead.ai_arv_low)} – ${fmtMoney(lead.ai_arv_high)}`}
              testid="val-arv-mid"
            />
            <Stat
              label="Repairs"
              value={fmtMoney(lead.ai_repair_estimate)}
              sub="rehab budget"
              accent="text-rose-700"
              icon={<TrendingDown size={12} />}
              testid="val-repairs"
            />
            <Stat
              label="MAO"
              value={fmtMoney(lead.ai_mao)}
              sub="max offer"
              accent="text-emerald-700 font-extrabold"
              icon={<DollarSign size={12} />}
              big
              testid="val-mao"
            />
          </div>

          {/* MAO math breakdown */}
          <div className="bg-white/70 border rounded-lg p-3 mb-3 font-mono text-[11px] text-stone-700 leading-relaxed">
            <span className="text-stone-500">MAO = </span>
            ({fmtMoney(lead.ai_arv_mid)} × 0.70)
            − {fmtMoney(lead.ai_repair_estimate)}
            − {fmtMoney(lead.ai_assignment_fee || 10000)}
            <span className="text-emerald-700 font-bold"> = {fmtMoney(lead.ai_mao)}</span>
          </div>

          {lead.ai_valuation_reasoning && (
            <div className="text-sm text-stone-700 leading-relaxed mb-3">
              {lead.ai_valuation_reasoning}
            </div>
          )}

          {lead.ai_valuation_key_factors?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wide text-stone-500 mb-1.5">
                Key factors
              </div>
              <ul className="text-xs text-stone-700 space-y-1">
                {lead.ai_valuation_key_factors.map((f, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="text-emerald-600 shrink-0">•</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {lead.ai_valuated_at && (
            <div className="text-[10px] text-stone-500 mt-3">
              Valuated {new Date(lead.ai_valuated_at).toLocaleString()}
              {lead.ai_valuation_comp_count
                ? ` · ${lead.ai_valuation_comp_count} comps used`
                : ""}
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-stone-600">
          Get an instant ARV range + repair estimate + max offer price (using the
          70% rule). Powered by RentCast comps + Claude underwriting.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, accent = "", icon, big, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide text-stone-500 mb-1">
        {icon}
        {label}
      </div>
      <div className={`font-display ${big ? "text-3xl" : "text-2xl"} font-bold tabular-nums ${accent || "text-stone-900"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}
